/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useCalendarDataContext } from './CalendarDataContext';
import { SIDEBAR_TYPE, SIDEBAR_MODE } from '../constants/types';

export const SidebarContext = createContext(null);

/**
 * Provider for sidebar state - manages create/edit forms for events and milestones
 */
export function SidebarProvider({ children }) {
  const { events, milestones } = useCalendarDataContext();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarType, setSidebarType] = useState(SIDEBAR_TYPE.EVENT);
  const [sidebarMode, setSidebarMode] = useState(SIDEBAR_MODE.CREATE);
  const [sidebarData, setSidebarData] = useState(null);
  const [previewChanges, setPreviewChanges] = useState(null);
  
  const openSidebar = useCallback((type, mode, data) => {
    setSidebarType(type);
    setSidebarMode(mode);
    setSidebarData(data);
    setPreviewChanges(null);
    setSidebarOpen(true);
  }, []);
  
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    setSidebarData(null);
    setPreviewChanges(null);
  }, []);
  
  const handleSidebarChange = useCallback((changes) => {
    setPreviewChanges(changes);
  }, []);
  
  // Click handlers for opening sidebar in edit mode
  const handleEventClick = useCallback((event) => {
    const originalEvent = events.find(e => e.id === event.id);
    if (originalEvent) {
      setSidebarType(SIDEBAR_TYPE.EVENT);
      setSidebarMode(SIDEBAR_MODE.EDIT);
      setSidebarData(originalEvent);
      setPreviewChanges(null);
      setSidebarOpen(true);
    }
  }, [events]);
  
  const handleMilestoneClick = useCallback((milestone) => {
    const originalMilestone = milestones.find(m => m.id === milestone.id);
    if (originalMilestone) {
      setSidebarType(SIDEBAR_TYPE.MILESTONE);
      setSidebarMode(SIDEBAR_MODE.EDIT);
      setSidebarData(originalMilestone);
      setPreviewChanges(null);
      setSidebarOpen(true);
    }
  }, [milestones]);
  
  const value = useMemo(() => ({
    sidebarOpen,
    sidebarType,
    sidebarMode,
    sidebarData,
    previewChanges,
    openSidebar,
    closeSidebar,
    onSidebarChange: handleSidebarChange,
    onEventClick: handleEventClick,
    onMilestoneClick: handleMilestoneClick,
  }), [
    sidebarOpen,
    sidebarType,
    sidebarMode,
    sidebarData,
    previewChanges,
    openSidebar,
    closeSidebar,
    handleSidebarChange,
    handleEventClick,
    handleMilestoneClick,
  ]);
  
  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * Hook to access sidebar context
 */
export function useSidebarContext() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
}
